import { useState } from 'react';
import { Badge, Button, Card, FileButton, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconX } from '@tabler/icons-react';
import {
  APP_ROKU_DELIVERABLE_SLOTS,
  ASSET_DELIVERABLE_MAX_BYTES,
  type AssetDeliverableSlot,
} from '@/lib/constants/appFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { useCreateAssetSet, useFactoryAssetSets } from '../useAppFactory';

interface AppRokuManualSetProps {
  appCfgId: number;
  disabled?: boolean;
}

/**
 * The manual arm of the asset pipeline (ADR-0017): per-slot pickers build
 * ONE partial-or-full set that rides the same factory validation → proof
 * sheet → approve-publishes flow as the Brand Kit. Client checks are fast
 * feedback only — the factory re-validates authoritatively, and nothing
 * goes live before Approve & Publish.
 */
export function AppRokuManualSet({ appCfgId, disabled }: AppRokuManualSetProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId);
  const createMutation = useCreateAssetSet(appCfgId);
  const [picked, setPicked] = useState<Record<string, File>>({});

  // Gateway off → hide quietly (Brand Kit card behavior). While a set is
  // pending the customer decides THAT set first — one draft at a time.
  if (assetSetsQuery.isError) return null;
  const pending = (assetSetsQuery.data?.assetSets ?? []).some((s) => s.state === 'generated');
  if (pending) return null;

  const handlePick = async (slot: AssetDeliverableSlot, file: File | null) => {
    if (!file) return;
    const problem = await BrandKitUtils.validateSlotFile(file, slot, ASSET_DELIVERABLE_MAX_BYTES);
    if (problem) {
      notifications.show({ title: 'Wrong image', message: problem, color: 'red' });
      return;
    }
    setPicked((prev) => ({ ...prev, [slot.name]: file }));
  };

  const handleClear = (name: string) => {
    setPicked((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async () => {
    let files: Record<string, string>;
    try {
      const entries = await Promise.all(
        Object.entries(picked).map(
          async ([name, file]) => [name, await BrandKitUtils.fileToBase64(file)] as const,
        ),
      );
      files = Object.fromEntries(entries);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Could not read the files.',
        color: 'red',
      });
      return;
    }
    createMutation.mutate(
      { files },
      {
        onSuccess: () => {
          setPicked({});
          notifications.show({
            title: 'Set Uploaded',
            message: 'Review the preview and approve it to publish.',
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      },
    );
  };

  const groups = [...new Set(APP_ROKU_DELIVERABLE_SLOTS.map((s) => s.group))];
  const pickedCount = Object.keys(picked).length;

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconUpload size={20} />
          <Text fw={600} size="md">
            Upload Your Own Images
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          Prefer full control? Pick the images you want to replace — you don&apos;t need
          all of them. Sizes must match exactly. You review a preview before anything goes
          live.
        </Text>

        {groups.map((group) => (
          <Stack key={group} gap={6}>
            <Text size="sm" fw={500}>
              {group}
            </Text>
            <Group gap="xs" wrap="wrap">
              {APP_ROKU_DELIVERABLE_SLOTS.filter((s) => s.group === group).map((slot) => {
                const chosen = picked[slot.name];
                return (
                  <Group key={slot.name} gap={4}>
                    <FileButton
                      onChange={(file) => void handlePick(slot, file)}
                      accept={slot.format === 'png' ? 'image/png' : 'image/jpeg'}
                    >
                      {(props) => (
                        <Button size="compact-sm" variant={chosen ? 'light' : 'default'} {...props}>
                          {slot.label} · {slot.w}x{slot.h}
                        </Button>
                      )}
                    </FileButton>
                    {chosen && (
                      <Badge
                        variant="light"
                        color="green"
                        size="sm"
                        rightSection={
                          <IconX
                            size={12}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleClear(slot.name)}
                          />
                        }
                      >
                        {chosen.name}
                      </Badge>
                    )}
                  </Group>
                );
              })}
            </Group>
          </Stack>
        ))}

        <Group>
          <Button
            onClick={() => void handleSubmit()}
            loading={createMutation.isPending}
            disabled={disabled || pickedCount === 0}
          >
            Upload {pickedCount > 0 ? `${pickedCount} ` : ''}for Review
          </Button>
          <Text size="xs" c="dimmed">
            Uploads replace only the slots you pick; everything else stays as-is.
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}
